import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import useEsTelefono from "../../../../utils/esTelefono";
import { getAccessPermissions } from "../../../../common";
import EnrollmentUpdatesView, { TabKey, TABS } from "./enrollmentUpdatesView";
import { TabProps } from "./helpers";

export interface DeepLink { studentId?: number; enrollmentId?: number; scheduleId?: number; updateId?: number; tab?: TabKey; }

/*
	Enlaces directos (Estudiantes, Horarios, Avisos):
	  ?alumno=<studentId>&matricula=<enrollmentId>  abre Actualizar con la alumna
	  ?horario=<scheduleId>                         abre Actualizar, Un Horario
	  ?actualizacion=<id>                           abre Por Aplicar con esa actualización
	  ?tab=<pestaña> o #<pestaña>                   actualizar, aplicar, flotantes, historial
	Se leen una vez y se limpia la dirección para que al recargar no se repitan.
*/
// La pantalla se monta dos veces al abrir el enlace en una pestaña nueva (el menú se arma después de iniciar sesión):
// la segunda lectura ya no ve la dirección, así que se recuerda unos segundos.
let enlaceRecordado: { link: DeepLink; en: number } | null = null;

const readDeepLink = (): DeepLink => {
	const q = new URLSearchParams(window.location.search);
	const n = (k: string) => Number(q.get(k) || 0) || undefined;
	const pedida = (q.get("tab") || window.location.hash.replace("#", "")).toLowerCase();
	const tab = TABS.find((t) => t === pedida);
	const link: DeepLink = { studentId: n("alumno"), enrollmentId: n("matricula"), scheduleId: n("horario"), updateId: n("actualizacion"), tab };
	if (link.studentId || link.scheduleId || link.updateId || link.tab) {
		enlaceRecordado = { link, en: Date.now() };
		window.history.replaceState(null, "", window.location.pathname);
		return link;
	}
	if (enlaceRecordado && Date.now() - enlaceRecordado.en < 60000) { return enlaceRecordado.link; }
	return link;
};

/** "Actualizaciones" (sección enrollmentUpdates): actualizar, aplicar, flotantes e historial de matrículas. */
const EnrollmentUpdatesController: React.FC = () => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	// Plantel de la barra, no el de la sesión (mismo criterio que Matrículas, 2026-09-24).
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;
	const countryId: number | null = legacyCampus?.company?.country_id ?? (sessionCampus?.id === campusId ? sessionCampus?.countryId : null) ?? null;
	const campusName: string = legacyCampus?.name || "";

	const userProfile: any = useSelector((state: RootState) => state.userProfile.value);
	const canApplyNow: boolean = !!getAccessPermissions(userProfile, "enrollmentUpdates").update;
	const [link] = useState<DeepLink>(readDeepLink);
	// Si la pantalla se queda montada, el enlace ya se usó: no se repite al volver desde el menú.
	useEffect(() => { const t = setTimeout(() => { enlaceRecordado = null; }, 4000); return () => clearTimeout(t); }, []);
	const [tab, setTab] = useState<TabKey>(link.tab || (link.updateId ? "aplicar" : "actualizar"));
	const [counts, setCounts] = useState<{ pending: number; returned: number; floating: number }>({ pending: 0, returned: 0, floating: 0 });
	const [notice, setNotice] = useState<{ text: string; ok: boolean } | null>(null);
	const [tick, setTick] = useState<number>(0);

	// Los contadores se piden al abrir y cuando algo cambia aquí; nunca por reloj.
	useEffect(() => {
		if (campusId === null) { return; }
		let vigente = true;
		http.enrollmentUpdatesService.list(campusId, "pending", { perPage: 1 })
			.then((r) => { if (vigente && r.data.counts) { setCounts((c) => ({ ...c, ...r.data.counts })); } })
			.catch((e) => { if (vigente && e?.response?.status === 403) { setNotice({ text: e?.response?.data?.message || "Sin permiso.", ok: false }); } });
		return () => { vigente = false; };
	}, [campusId, tick]);

	if (campusId === null) { return <React.Fragment />; }

	const tabProps: TabProps = {
		campusId, countryId, movil, canApplyNow,
		notify: (text, ok = true) => setNotice({ text, ok }),
		changed: () => setTick((t) => t + 1)
	};
	return <EnrollmentUpdatesView campusName={campusName} movil={movil} tab={tab} onTab={(t) => { setTab(t); setNotice(null); try { window.history.replaceState(null, "", "#" + t); } catch (e) { /* sin historial */ } }}
		counts={counts} notice={notice} onCloseNotice={() => setNotice(null)} tabProps={tabProps} link={link} onGoTo={(t) => setTab(t)} />;
};

export default EnrollmentUpdatesController;
